// The device-level id abra mints for anonymous signal tracking (`a_psid`).
// Reading/minting it here — rather than only ever letting abra's own `/e`
// and `/ev` endpoints set it — lets it double as PostHog's bootstrap
// distinct id, so a visitor's PostHog events and signal events join on
// one key from the very first pageview. Mirrors abra's own
// `requestPersonSignalId` (src/api/helpers.ts) and after-www's
// `getOrCreateDistinctId` (app/utils/distinctId.server.ts).
//
// Anonymous device id only — NEVER pass this to posthog.identify. Person
// identity stays keyed on personId (see UserContext).

const COOKIE_NAME = "a_psid";
const MAX_AGE_SECONDS = 60 * 60 * 24 * 365 * 2; // 2 years, matching abra's cookie lifetime

function readCookie(name: string): string | null {
  try {
    const prefix = `${name}=`;
    const match = document.cookie
      .split("; ")
      .find((entry) => entry.startsWith(prefix));
    return match ? match.slice(prefix.length) : null;
  } catch {
    // Cookie access can throw (a sandboxed or opaque-origin document);
    // no id is better than no app.
    return null;
  }
}

function writeCookie(id: string): void {
  const parts = [
    `${COOKIE_NAME}=${id}`,
    "Path=/",
    `Max-Age=${MAX_AGE_SECONDS}`,
    "SameSite=Lax",
  ];
  // Cross-subdomain (and Secure) only on the real domain — a bare
  // `Domain=.after.com` attribute is rejected by browsers on localhost.
  if (window.location.hostname.endsWith(".after.com")) {
    parts.push("Domain=.after.com", "Secure");
  }
  try {
    document.cookie = parts.join("; ");
  } catch {
    // Unwritable cookies: the id lives for this page load only.
  }
}

/** A v4 uuid from whatever this browser offers: `randomUUID` needs a
 * secure context, `getRandomValues` is older and everywhere; with neither
 * there is no id worth minting. */
function mintId(): string | null {
  try {
    if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
    return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex
      .slice(6, 8)
      .join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
  } catch {
    return null;
  }
}

/**
 * Reads this browser's `a_psid` cookie, minting and persisting one if it
 * has never carried it. Null only when the browser offers no way to read
 * or make one — PostHog then falls back to its own anonymous id. This runs
 * at module load, before anything renders, so it must never throw.
 */
export function readOrMintPersonSignalId(): string | null {
  const existing = readCookie(COOKIE_NAME);
  if (existing) return existing;

  const id = mintId();
  if (id) writeCookie(id);
  return id;
}
